import * as React from "react";

import { cn } from "@/lib/utils";

import { Input } from "./input";

export interface LabeledInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Accessible label text. Always rendered as a real ``<label>``. */
  label: string;
  /**
   * Visually hide the label (it stays in the accessibility tree).
   * For compact layouts that show the same text as a placeholder;
   * a placeholder alone is not a label (WCAG 2.2 — 1.3.1, 3.3.2,
   * 4.1.2): it disappears on input and many screen readers skip it.
   */
  hideLabel?: boolean;
  labelClassName?: string;
}

/** ``<Input>`` with an associated ``<label>`` (auto-generated id). */
export const LabeledInput = React.forwardRef<HTMLInputElement, LabeledInputProps>(
  ({ label, hideLabel = true, labelClassName, id, ...props }, ref) => {
    const autoId = React.useId();
    const inputId = id ?? autoId;
    return (
      <>
        <label
          htmlFor={inputId}
          className={cn(hideLabel ? "sr-only" : "text-sm", labelClassName)}
        >
          {label}
        </label>
        <Input ref={ref} id={inputId} {...props} />
      </>
    );
  },
);
LabeledInput.displayName = "LabeledInput";
